import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  MINE_PARAM,
  PROJECT_POST_KEYS,
  PROJECT_POSTS,
} from '@brickup/shared/constants'
import { NAV } from '@brickup/shared/locales/de/nav'
import { PROJECTS } from '@brickup/shared/locales/de/projects'
import { Button, ReadOnlyScope } from '@/components/base'
import {
  listSearchValidator,
  useListFilters,
} from '@/components/Internal/DataTable'
import { PageLayout } from '@/components/Internal/PageLayout'
import { CreateProjectModal } from '@/components/Internal/Projects/CreateProjectModal'
import { InternalProjectsList } from '@/components/Internal/Projects/InternalProjectsList'
import { projectListFilters } from '@/components/Internal/Projects/project-list-filters'
import {
  useCanCreateProject,
  useIsProjectScoped,
} from '@/hooks/use-project-access'

export const Route = createFileRoute('/internal/projects/')({
  validateSearch: listSearchValidator(projectListFilters),
  component: ProjectsScreen,
})

function ProjectsScreen() {
  const filters = useListFilters(projectListFilters, Route)
  const [creating, setCreating] = useState(false)
  const canCreate = useCanCreateProject()
  useOwnProjectsLanding(filters.set)

  return (
    <PageLayout title={NAV.projects}>
      <InternalProjectsList
        filters={filters}
        // Reading the list is every internal role's; starting a project is
        // the advisor's.
        actions={
          <ReadOnlyScope readOnly={!canCreate}>
            <Button size="sm" icon={Plus} onClick={() => setCreating(true)}>
              {PROJECTS.create}
            </Button>
            <CreateProjectModal
              open={creating}
              onClose={() => setCreating(false)}
            />
          </ReadOnlyScope>
        }
      />
    </PageLayout>
  )
}

/**
 * Whether this session has already landed the advisor on their own projects.
 *
 * Outside the component, because the screen unmounts whenever a project is
 * opened: held in a ref, the landing would run again on the way back and
 * overwrite the reader's own "Alle" — which is spelled as an absent parameter
 * and so is indistinguishable from never having chosen.
 */
let landed = false

/**
 * A reader held to their own projects opens the list on them.
 *
 * The `mine` filter rather than one of the three posts: it matches whichever
 * post the project names them for, and the access they were given beside it,
 * which is what the reader means by their own.
 *
 * Written into the URL rather than resolved from an absent parameter: the
 * widest value is what the schema calls the default, so reading an absent one
 * as "mine" would leave "Alle" unspellable — the route validator drops a
 * parameter that equals the default.
 */
function useOwnProjectsLanding(
  set: (key: typeof MINE_PARAM, value: boolean) => void,
) {
  const scoped = useIsProjectScoped()
  const search = Route.useSearch()
  // A link that already names somebody is the sender's view, not a first
  // visit: narrowing it to the reader's own would show them the intersection,
  // which is usually nothing.
  const asked =
    search[MINE_PARAM] !== undefined ||
    PROJECT_POST_KEYS.some(post => search[PROJECT_POSTS[post].param])

  useEffect(() => {
    if (landed || !scoped) return
    landed = true
    if (!asked) set(MINE_PARAM, true)
  }, [scoped, asked, set])
}
